/**
 * SPEC.md §7 — the topbar's Recent menu. M6.4 (issue #31): every wholesale
 * buffer replacement shelves the outgoing edited buffer to `plotter:history`
 * first; this menu is where that history becomes visible and restorable.
 *
 * Mirrors `NewMenu.tsx`'s shape — a `useMenu`-based dropdown that only
 * reports a selection, App.tsx owns what happens to the source. Unlike
 * Export/Copy, the trigger is never disabled: Recent doesn't depend on the
 * current buffer having any content, only on `plotter:history` having rows.
 * `readHistory()` only runs while the menu is open, same reasoning as
 * `ShareMenu.tsx`'s `encodeShareState` gating — a closed dropdown has no
 * reason to re-read and re-parse localStorage on every App.tsx re-render.
 */
import { History } from 'lucide-react';
import { readHistory, type HistoryRow } from '../lib/history';
import { useMenu } from './useMenu';

interface RecentMenuProps {
  onRestore: (row: HistoryRow) => void;
}

export default function RecentMenu({ onRestore }: RecentMenuProps) {
  const { isOpen, containerRef, toggle, close } = useMenu<HTMLDivElement>();
  const rows = isOpen ? readHistory() : [];

  function handleSelect(row: HistoryRow) {
    onRestore(row);
    close();
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggle}
        className="flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-text-ink hover:bg-ink-3"
      >
        <History size={13} aria-hidden />
        Recent
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Recent diagrams"
          className="absolute left-0 top-full z-10 mt-1 max-h-[70vh] w-64 overflow-y-auto rounded-sm border border-ink-3 bg-ink-2 py-1 shadow-lg"
        >
          {rows.length === 0 ? (
            <p className="px-3 py-1.5 text-xs text-mute">No recent diagrams yet.</p>
          ) : (
            rows.map((row) => (
              <button
                key={row.id}
                type="button"
                role="menuitem"
                onClick={() => handleSelect(row)}
                className="block w-full truncate px-3 py-1.5 text-left text-xs text-text-ink hover:bg-ink-3"
              >
                {row.title}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
